"use client";

import { SplitPage } from "@/utils/splitPages";
import React from "react";

export type SlideStackStrategy = "all" | "deferred" | "windowed";

interface SlideStackProps {
  pages: SplitPage[];
  pageHeight: number;
  currentPage?: number;
  strategy?: SlideStackStrategy;
  windowSize?: number;
  renderPage: (page: SplitPage, index: number) => React.ReactNode;
}

export default function SlideStack({
  pages,
  pageHeight,
  currentPage = 0,
  strategy = "all",
  windowSize = 3,
  renderPage,
}: SlideStackProps) {
  return (
    <div className="flex flex-col">
      {pages.map((page, index) => {
        const key = `${page.slideId}-${page.pageIndex}`;
        const height = pageHeight || undefined;
        const heightStyle = height ? { height } : { height: "100%" as const };

        if (strategy === "windowed" && height) {
          const inWindow = Math.abs(index - currentPage) <= windowSize;
          return (
            <div
              key={key}
              className="snap-start snap-always shrink-0"
              style={heightStyle}
              aria-hidden={!inWindow}
            >
              {inWindow ? renderPage(page, index) : null}
            </div>
          );
        }

        if (strategy === "deferred" && height) {
          return (
            <div
              key={key}
              className="snap-start snap-always shrink-0"
              style={
                {
                  ...heightStyle,
                  contentVisibility: "auto",
                  containIntrinsicSize: `${height}px`,
                } as React.CSSProperties
              }
            >
              {renderPage(page, index)}
            </div>
          );
        }

        return (
          <div
            key={key}
            className="snap-start snap-always shrink-0"
            style={heightStyle}
          >
            {renderPage(page, index)}
          </div>
        );
      })}
    </div>
  );
}
